import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { message } from 'antd';
import { LabeledValue } from 'antd/lib/select';
import { ResultApi } from '../../common/services/AppClient';
import { BusinessRequest, BusinessDto, BusinessPartnerDto, IBusinessSearchStoreBase } from '../types';
import { Disposer, InstanceUtils } from '../../custom_shared/misc';
import AppStoreBase from './AppStoreBase';
import SourceStore from './SourceStore';

interface IBusinessSearchService {
    searchBusinessPartners(
        sessionExtension: string,
        sessionId: string,
        ignoreCache?: boolean
    ): Promise<ResultApi<BusinessPartnerDto[]>>;
    searchBusinesses(
        sessionExtension: string,
        sessionId: string,
        request: BusinessRequest
    ): Promise<ResultApi<BusinessDto[]>>;
}

export default abstract class BusinessSearchStoreBase extends Disposer implements IBusinessSearchStoreBase {
    modalVisible: boolean = false;
    businessPartners: BusinessPartnerDto[] = [];
    businessPartnersLoading: boolean = false;

    businesses: BusinessDto[] = [];
    businessesLoading: boolean = false;

    page: number = 0;
    pageSize: number = 10;

    readonly requestPage: number = 0;
    readonly requestPageSize: number = 1000;
    readonly sqlScriptIds: string[] = [
        'SqlScripts_BusinessSearchAndOperatorScriptId',
        'SqlScripts_BusinessSearchOrOperatorScriptId',
        'SqlScripts_BusinessPartnersScriptId'
    ];

    constructor(
        protected readonly appStore: AppStoreBase,
        protected readonly sourceStore: SourceStore,
        protected readonly service: IBusinessSearchService
    ) {
        super();

        makeObservable(this, {
            businessPartners: observable,
            businesses: observable,
            businessPartnersLoading: observable,
            businessesLoading: observable,
            modalVisible: observable,
            page: observable,
            pageSize: observable,
            setModalVisible: action,
            setBusinessPartners: action,
            setBusinesses: action,
            businessPartnersOptions: computed,
            typeOfBusinessOptions: computed
        });
    }

    updatePaging(page: number, pageSize: number) {
        runInAction(() => {
            this.page = page;
            this.pageSize = pageSize;
        });
    }

    setModalVisible(modalVisible: boolean) {
        this.modalVisible = modalVisible;
    }

    setBusinessPartners(businessPartners: BusinessPartnerDto[]) {
        this.businessPartners = businessPartners;
    }

    setBusinesses(businesses: BusinessDto[]) {
        this.businesses = businesses;
    }

    async getBusinessPartners(ignoreCache?: boolean) {
        try {
            runInAction(() => {
                this.businessPartnersLoading = true;
            });

            const resp = await this.service.searchBusinessPartners(
                this.appStore.sessionExtension,
                this.appStore.sessionId,
                ignoreCache
            );

            if (resp.isErr()) {
                return;
            }

            resp.map(businessPartners => this.setBusinessPartners(businessPartners));
        } finally {
            runInAction(() => {
                this.businessPartnersLoading = false;
            });
        }
    }

    async getBusinesses(filter: Omit<BusinessRequest, 'pageNumber' | 'pageSize'>) {
        try {
            runInAction(() => {
                this.businessesLoading = true;
            });

            const resp = await this.service.searchBusinesses(this.appStore.sessionExtension, this.appStore.sessionId, {
                ...filter,
                pageNumber: this.requestPage,
                pageSize: this.requestPageSize
            });

            if (resp.isErr()) {
                message.error('Business search is currently unavailable. Please try again later');
                return;
            }

            resp.map(businesses => this.setBusinesses(businesses));
        } finally {
            runInAction(() => {
                this.businessesLoading = false;
            });
        }
    }

    get businessPartnersOptions(): LabeledValue[] {
        return this.businessPartners.map(p => ({ label: p.text, value: p.value, title: p.text }));
    }

    get typeOfBusinessOptions(): LabeledValue[] {
        const businessTypesSource = this.sourceStore.sources.find(s => s.id === 'businessTypes');

        if (!InstanceUtils.isStaticSource(businessTypesSource)) {
            return [];
        }

        return businessTypesSource.data.map(d => ({ label: d.text, value: d.text, title: d.text }));
    }

    dispose() {
        this.disposeReactions();
    }
}
